import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  ArrowLeft,
  Save,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Tag,
  Zap,
  ExternalLink,
  Upload,
  Server,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface PackageDetails {
  package_id: string
  slug: string
  name: string
  summary: string
  description: string
  capabilities: string[]
  tags: string[]
  server_url: string
  repository_url: string
  documentation_url: string
  review_status: string
  health_status: string
  health_checked_at: string
}

export default function PublisherPackageSettingsPage() {
  const { packageId } = useParams<{ packageId: string }>()
  const { token } = usePublisherAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [healthChecking, setHealthChecking] = useState(false)

  const [formData, setFormData] = useState<PackageDetails>({
    package_id: '',
    slug: '',
    name: '',
    summary: '',
    description: '',
    capabilities: [],
    tags: [],
    server_url: '',
    repository_url: '',
    documentation_url: '',
    review_status: '',
    health_status: '',
    health_checked_at: '',
  })

  const [newCapability, setNewCapability] = useState('')
  const [newTag, setNewTag] = useState('')
  const [capError, setCapError] = useState('')

  useEffect(() => {
    loadPackage()
  }, [packageId, token])

  const loadPackage = async () => {
    if (!token || !packageId) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/v1/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Package not found')
      const data = await res.json()
      setFormData({
        package_id: data.package_id || packageId,
        slug: data.slug || '',
        name: data.name || '',
        summary: data.summary || '',
        description: data.description || '',
        capabilities: data.capabilities || [],
        tags: data.tags || [],
        server_url: data.server_url || '',
        repository_url: data.repo_url || '',
        documentation_url: data.docs_url || '',
        review_status: data.trust_tier || data.review_status || '',
        health_status: data.health_status || '',
        health_checked_at: data.health_checked_at || '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load package')
    } finally {
      setLoading(false)
    }
  }

  const addCapability = () => {
    setCapError('')
    if (!newCapability) return
    if (!/^[a-z][a-z0-9]*\.[a-z][a-z0-9_]*$/.test(newCapability)) {
      setCapError('Must be namespace.action format (e.g., weather.forecast)')
      return
    }
    if (!formData.capabilities.includes(newCapability)) {
      setFormData((prev) => ({
        ...prev,
        capabilities: [...prev.capabilities, newCapability],
      }))
      setNewCapability('')
    }
  }

  const removeCapability = (cap: string) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: prev.capabilities.filter((c) => c !== cap),
    }))
  }

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag.toLowerCase())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.toLowerCase()],
      }))
      setNewTag('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }))
  }

  const handleSave = async () => {
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      // Update package - currently the API doesn't have a PATCH endpoint for packages
      // so we create a new version with updated metadata
      // For now, we save what we can via the create endpoint pattern
      const res = await fetch(`${API_BASE_URL}/v1/packages/${formData.package_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          summary: formData.summary,
          description: formData.description,
          capabilities: formData.capabilities,
          tags: formData.tags,
          server_url: formData.server_url,
          repo_url: formData.repository_url,
          docs_url: formData.documentation_url,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to save')
      }

      setSuccess('Package settings saved successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleHealthCheck = async () => {
    if (!formData.server_url) {
      setError('Please set a Server URL first')
      return
    }
    setHealthChecking(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/packages/${formData.package_id}/health-check`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )
      if (!res.ok) throw new Error('Health check failed')
      const data = await res.json()
      setFormData((prev) => ({
        ...prev,
        health_status: data.health_status || 'unknown',
        health_checked_at: data.checked_at || new Date().toISOString(),
      }))
    } catch {
      setFormData((prev) => ({
        ...prev,
        health_status: 'unhealthy',
        health_checked_at: new Date().toISOString(),
      }))
    } finally {
      setHealthChecking(false)
    }
  }

  const handleDelete = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/packages/${formData.package_id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to delete')
      }
      navigate('/publisher/packages')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete package')
    }
  }

  const healthIcon = () => {
    switch (formData.health_status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'unhealthy':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/publisher/packages')}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to packages
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Package Settings</h1>
            <p className="text-gray-500 mt-1">{formData.name || 'New Package'}</p>
          </div>
          {formData.slug && (
            <Link
              to={`/skills/${formData.slug}`}
              className="btn btn-secondary"
              target="_blank"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Public Page
            </Link>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 text-green-700">
          <Save className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                disabled
                className="input w-full bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Slug cannot be changed after creation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Summary
              </label>
              <input
                type="text"
                value={formData.summary}
                onChange={(e) => setFormData((prev) => ({ ...prev, summary: e.target.value }))}
                className="input w-full"
                maxLength={160}
              />
              <p className="text-xs text-gray-400 mt-1">
                {formData.summary.length}/160 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="input w-full h-32"
              />
            </div>
          </div>
        </div>

        {/* Server URL & Health Check */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <Server className="w-5 h-5 inline mr-2" />
            MCP Server
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Server URL
              </label>
              <input
                type="url"
                value={formData.server_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, server_url: e.target.value }))
                }
                className="input w-full"
                placeholder="https://your-server.example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                The URL where your MCP server is hosted. Users will connect to this endpoint.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {healthIcon()}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Health Status:{' '}
                    <span
                      className={
                        formData.health_status === 'healthy'
                          ? 'text-green-600'
                          : formData.health_status === 'unhealthy'
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }
                    >
                      {formData.health_status || 'Not checked'}
                    </span>
                  </p>
                  {formData.health_checked_at && (
                    <p className="text-xs text-gray-400">
                      Last checked:{' '}
                      {new Date(formData.health_checked_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleHealthCheck}
                disabled={healthChecking || !formData.server_url}
                className="btn btn-secondary text-sm inline-flex items-center gap-1"
              >
                {healthChecking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Check
              </button>
            </div>

            {formData.health_status === 'unhealthy' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Your server is not responding. Packages with unhealthy servers will show a warning
                to users and may be hidden from search results after 6 hours.
              </div>
            )}
          </div>
        </div>

        {/* Capabilities */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <Zap className="w-5 h-5 inline mr-2" />
            Capabilities
          </h2>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={newCapability}
              onChange={(e) => {
                setNewCapability(e.target.value)
                setCapError('')
              }}
              onKeyPress={(e) =>
                e.key === 'Enter' && (e.preventDefault(), addCapability())
              }
              className={`input flex-1 ${capError ? 'border-red-300' : ''}`}
              placeholder="namespace.action (e.g., weather.forecast)"
            />
            <button type="button" onClick={addCapability} className="btn btn-secondary">
              Add
            </button>
          </div>
          {capError && (
            <p className="text-xs text-red-500 mb-2">{capError}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {formData.capabilities.map((cap) => (
              <span
                key={cap}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
              >
                {cap}
                <button
                  type="button"
                  onClick={() => removeCapability(cap)}
                  className="ml-2 hover:text-primary-900"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <Tag className="w-5 h-5 inline mr-2" />
            Tags
          </h2>
          <div className="flex space-x-2 mb-3">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) =>
                e.key === 'Enter' && (e.preventDefault(), addTag())
              }
              className="input flex-1"
              placeholder="Add a tag"
            />
            <button type="button" onClick={addTag} className="btn btn-secondary">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-2 hover:text-gray-900"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Links */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Links</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repository URL
              </label>
              <input
                type="url"
                value={formData.repository_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, repository_url: e.target.value }))
                }
                className="input w-full"
                placeholder="https://github.com/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documentation URL
              </label>
              <input
                type="url"
                value={formData.documentation_url}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    documentation_url: e.target.value,
                  }))
                }
                className="input w-full"
                placeholder="https://docs.example.com/..."
              />
            </div>
          </div>
        </div>

        {/* New Version */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <Upload className="w-5 h-5 inline mr-2" />
            Publish New Version
          </h2>
          <p className="text-gray-500 mb-4">
            Upload a new version of your package to submit for review.
          </p>
          <button className="btn btn-secondary">Upload Version</button>
        </div>

        {/* Danger Zone */}
        <div className="card p-6 border-red-200">
          <h2 className="text-lg font-semibold text-red-600 mb-4">
            <AlertTriangle className="w-5 h-5 inline mr-2" />
            Danger Zone
          </h2>
          <p className="text-gray-500 mb-4">
            Deleting a package is permanent and cannot be undone.
          </p>
          {showDeleteConfirm ? (
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Are you sure?</span>
              <button
                onClick={handleDelete}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Package
            </button>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
